import React, {
  FC,
  ReactElement,
  useEffect,
  useState,
  ChangeEvent,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  PsCheckboxTuner,
  PsNumericTuner,
  PsRadioTuner,
  PsSelectTuner,
  PsTextareaTuner,
  PsTextTuner,
} from 'stencilSetup';

interface CheckboxItems {
  attribute: string;
  value: string;
}

interface Option {
  key: string | number;
  value: string;
  label: string;
}

export interface FormElementProps {
  inputId: string;
  formField: string;
  label: string;
  name?: string;
  value?: any;
  required?: boolean;
  disabled?: boolean;
  checked?: boolean;
  options?: Option[];
  optionOne?: string;
  otherData?: CheckboxItems[];
  onValidityChange?: (valid: boolean, event?: string) => void;
  isInvalid?: boolean;
  detectChange?: any;
  passEvent?: (eventType: string) => void;
  icon?: string;
  iconLeft?: boolean;
  onChange?: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  controlled?: boolean;
  maxLength?: number;
  minLength?: number;
  validationText?: string;
  originalValue?: any;
  hideFirstOption?: boolean;
  pattern?: string;
  // eslint-disable-next-line
  [key: string]: any; // Wildcard prop to accept any additional props
}

export const FormElement: FC<FormElementProps> = ({
  inputId,
  formField,
  label,
  name,
  value,
  required = false,
  disabled = false,
  checked,
  options,
  optionOne,
  otherData,
  onValidityChange,
  isInvalid,
  detectChange,
  passEvent,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  icon,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  iconLeft,
  onChange,
  controlled = false,
  maxLength,
  minLength,
  originalValue,
  hideFirstOption = false,
  pattern,
  ...rest // Wildcard prop to accept any additional props
}) => {
  const { t } = useTranslation('common');
  const [inputValue, setInputValue] = useState<string>(value || '');
  const [inputInvalid, setInputInvalid] = useState<boolean>(false);

  useEffect(() => {
    // Sync internal state with prop
    setInputInvalid(isInvalid || false);
  }, [isInvalid]);

  const isElementInvalid = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (formField === 'checkbox' || formField === 'radio') {
      return !(e.target as HTMLInputElement).checked;
    } else {
      if (originalValue) {
        if (e.target.value !== originalValue.value) {
          return (
            e.target.validity.patternMismatch ||
            e.target.value.trim() === '' ||
            e.target.value === undefined
          );
        } else {
          return e.target.value.trim() === '' || e.target.value === undefined;
        }
      } else {
        return (
          e.target.validity.patternMismatch ||
          e.target.value.trim() === '' ||
          e.target.value === undefined
        );
      }
    }
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    if (passEvent) {
      passEvent(e as any);
    }

    // call detectChange prop
    if (detectChange) {
      const isChecked = (e.target as HTMLInputElement).checked;
      detectChange(isChecked);
    }

    // call onChange prop
    if (onChange) {
      onChange(e);
    }

    // set input value
    setInputValue(e.target.value);
    const isInputInvalid = required && isElementInvalid(e);
    setInputInvalid(isInputInvalid);
    if (onValidityChange) {
      onValidityChange(!isInputInvalid);
    }

    // additional check for pattern validation fields
    if (
      e.target.name === 'ssn' ||
      e.target.name === 'sin' ||
      e.target.name.includes('phone')
    ) {
      if (originalValue.value !== e.target.value) {
        // due to masking, we don't want to validate if nothing has changed, because the value could have x's in it
        const isValid = e.target.checkValidity();
        setInputInvalid(!isValid);
        if (onValidityChange) {
          onValidityChange(isValid);
        }
      }
    }

    if (e.target.name === 'email') {
      // browsers think that emails don't require extensions, so we need to validate them ourselves
      if (originalValue.value !== e.target.value) {
        const pattern = new RegExp('[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$');
        const isValid = pattern.test(e.target.value);
        setInputInvalid(!isValid);
        if (onValidityChange) {
          onValidityChange(isValid);
        }
      }
    }
  };

  let formElement: ReactElement | null = null;
  originalValue = { value };
  if (['text', 'email', 'password', 'tel'].includes(formField)) {
    // customize validation text to reflect what could be wrong besides just "required"
    let validationMessage = t('formValidation.fieldCannotBeEmpty'); // Default validation message
    if (name === 'ssn') {
      validationMessage = t('formValidation.invalidSSN');
    } else if (name === 'sin') {
      validationMessage = t('formValidation.invalidSIN');
    } else if (name?.includes('phone')) {
      // phone number validation
      validationMessage = t('formValidation.invalidPhone');
    } else if (name === 'email') {
      validationMessage = t('formValidation.invalidEmail');
    }

    formElement = (
      <PsTextTuner
        inputId={inputId}
        type={formField}
        name={name}
        label={label}
        value={controlled ? value : inputValue}
        required={required}
        aria-invalid={inputInvalid}
        validationText={validationMessage}
        disabled={disabled}
        maxlength={maxLength?.toString()}
        minlength={minLength?.toString()}
        pattern={pattern}
        onBlur={(e) => handleInputChange(e as any)}
        {...rest}
      />
    );
  }

  if (formField === 'date') {
    formElement = (
      <PsTextTuner
        inputId={inputId}
        type="date"
        name={name}
        label={label}
        value={value ? value : ''}
        required={required}
        aria-invalid={inputInvalid}
        validationText={t('formValidation.fieldCannotBeEmpty')}
        disabled={disabled}
        onBlur={(e) => handleInputChange(e as any)}
        onInput={(e) => handleInputChange(e as any)}
        {...rest}
      />
    );
  }

  if (formField === 'number' || formField === 'NUMBER') {
    const isSalaryField = inputId.includes('salary') || inputId.includes('pay');
    const iconLeft = true;
    formElement = (
      <PsNumericTuner
        inputId={inputId}
        name={name}
        label={label}
        value={controlled ? value : inputValue}
        required={required}
        aria-invalid={inputInvalid}
        validationText={t('formValidation.fieldCannotBeEmpty')}
        disabled={disabled}
        icon={isSalaryField ? 'currency-dollar' : icon}
        onBlur={(e) => handleInputChange(e as any)}
        {...(isSalaryField ? { iconLeft } : {})}
        {...rest}
      />
    );
  }

  if (formField === 'checkbox' || formField === 'boolean') {
    formElement = (
      <PsCheckboxTuner
        inputId={inputId}
        label={label}
        name={inputId}
        value={inputValue}
        required={required}
        validationText={t('formValidation.required')}
        checked={checked}
        isInvalid={inputInvalid}
        otherData={otherData}
        disabled={disabled}
        key={inputId}
        onPsChange={(e) => handleInputChange(e.detail.event as any)}
        onPsBlur={(e) => handleInputChange(e.detail.event as any)}
        {...rest}
      />
    );
  }

  if (formField === 'radio') {
    formElement = (
      <PsRadioTuner
        inputId={inputId}
        label={label}
        name={name || 'radio'}
        value={inputValue}
        required={required}
        validationText={t('formValidation.required')}
        checked={checked}
        isInvalid={inputInvalid}
        otherData={otherData}
        disabled={disabled}
        onPsChange={(e) => handleInputChange(e.detail.event as any)}
        onPsBlur={(e) => handleInputChange(e.detail.event as any)}
        {...rest}
      />
    );
  }

  if (formField === 'select') {
    formElement = (
      <PsSelectTuner
        inputId={inputId}
        label={label}
        required={required}
        validationText="Required"
        value={value}
        aria-invalid={inputInvalid}
        disabled={disabled}
        firstOption={hideFirstOption}
        onPsChange={(e) => handleInputChange(e.detail.event as any)}
        onPsBlur={(e) => handleInputChange(e.detail.event as any)}
        {...rest}
      >
        {!hideFirstOption && (
          <option value="">
            {optionOne ? optionOne : `${t('select')} ${label}`}
          </option>
        )}
        {options?.map((option, index) => (
          <option
            key={index}
            value={option.value}
            selected={option.value === value}
          >
            {option.label}
          </option>
        ))}
      </PsSelectTuner>
    );
  }

  if (formField === 'textarea') {
    formElement = (
      <PsTextareaTuner
        inputId={inputId}
        label={label}
        required={required}
        validationText={t('formValidation.fieldCannotBeEmpty')}
        aria-invalid={inputInvalid}
        disabled={disabled}
        onBlur={(e) => handleInputChange(e as any)}
        {...rest}
      >
        {value}
      </PsTextareaTuner>
    );
  }

  return <>{formElement}</>;
};
